import { literal, z } from 'zod'
import { UserState } from '../types'

// For models that have `document` property
export const DocumentSchema = z.object({
  extension: z.string(),
  filename: z.string(),
  extension_is_previewable: z.boolean(),
  uploaded_by: z.number(),
  source: z.string(),
  date_uploaded: z.string(),
  file_size: z.number().nullable(),
  preview_size: z.number().nullable(),
  num_pages: z.number().nullable(),
})

export const RegionSchema = z.object({
  id: z.number(),
  name: z.string(),
  host: z.string(),
})

export const DMSSchema = z.object({
  id: z.number(),
  title: z.string(),
  external_id: z.string(),
})

export const CurrentUserSchema = z.object({
  id: z.number(),
  auth_user_id: z.number(),
  email: z.string(),
  first_name: z.string(),
  last_name: z.string(),
  is_admin: z.boolean().default(false),
  locale: z.string(),
  organization_id: z.number(),
  usership_ids: z.array(z.number()).default([]),
  dms: DMSSchema.optional().nullable(),
})

// This user data when we hit auth's api/v1/users
export const UserSchema = z.object({
  id: z.number(),
  email: z.string(),
  first_name: z.string(),
  last_name: z.string(),
  organization_id: z.number(),
  state: z.enum(UserState),
  regions: z.array(RegionSchema),
  locale: z.string(),
  default_email_frequency: z.string(),
  notify_about_email_uploads: z.boolean(),
  date_joined: z.string(),
  feature_flags: z.array(z.string()),
  dms: DMSSchema.optional().nullable(),
})

// This is user data when we hit dealrooms' api/users or /api/users/by_auth_id
export const DealroomUserSchema = z.object({
  id: z.number(),
  auth_user_id: z.number(),
  first_name: z.string(),
  last_name: z.string(),
  email: z.string(),
  state: z.enum(UserState),
  date_joined: z.string().nullable().optional(),
  deletion_date: z.string().nullable().optional(),
  is_admin: z.boolean().optional(),
  is_external: z.boolean().optional(),
  organization_title: z.string().optional(),
})

export const CandidateResponseSchema = z.object({
  users: z.array(
    UserSchema.pick({
      id: true,
      email: true,
      first_name: true,
      last_name: true,
    }),
  ),
})

export const TokenSchema = z.object({
  user_id: z.number(),
  email: z.string(),
  exp_access: z.number(),
  exp_refresh: z.number(),
  iat_access: z.number(),
  iat_refresh: z.number(),
  organization_id: z.number(),
  regions: z.array(RegionSchema),
  subscription_region_id: z.number().nullable(),
  tfa_secret_uri: z.string().optional(),
  tfa_status: z.union([
    z.literal('disabled'),
    z.literal('enabled'),
    z.literal('enforced'),
    z.literal('verified'),
    z.literal('sso_verified'),
  ]),
})

export const RegistrationSchema = z.object({
  id: z.number().optional(),
  email: z.string(),
  expiration: z.string(),
  is_external: z.boolean().optional(), // TODO: Request this field from BE
  activation_key: z.string().optional(),
  invited_by_id: z.number().optional(),
})

export const OrganizationRegistrationSchema = z.object({
  id: z.number().optional(),
  organization_id: z.number(),
  registration_id: z.number(),
  registration_email: z.string(),
  registration_expiration: z.string(),
  is_internal: z.boolean(),
})

export const DealStatusSchema = z.object({
  id: z.number().nullable().optional(),
  english_label: z.string().nullable(),
  french_label: z.string().nullable(),
  colour: z.number().nullable(),
  is_default: z.boolean().nullable(),
  organization_id: z.number().nullable(),
})

export const SelectOptionSchema = z.object({
  id: z.number(),
  english_label: z.string(),
  french_label: z.string(),
})

export const TextConfigSchema = z.object({
  max_length: z.number().nullable(),
  default_value: z.string(),
})

export const NumberConfigSchema = z.object({
  min_value: z.number().nullable(),
  max_value: z.number().nullable(),
  default_value: z.number().nullable(),
})

export const SingleSelectConfigSchema = z.object({
  options: z.array(SelectOptionSchema),
})

// Config is parsed loosely — the frontend reads specific keys
// based on field_type rather than relying on Zod to discriminate.
// A bare z.union cannot reliably distinguish these overlapping shapes.
export const DealFieldConfigSchema = z.record(z.string(), z.unknown())

export const DealFieldSchema = z.object({
  deal_field_id: z.number(),
  deal_field_set_id: z.number(),
  deal_field_type: z.enum(['text', 'number', 'single-select']),
  english_label: z.string(),
  french_label: z.string(),
  state: z.enum(['enabled', 'disabled', 'required']),
  system_field_key: z.enum(['none', 'matter-number', 'client-number']),
  config: DealFieldConfigSchema,
})

export const DealFieldListResponseSchema = z.object({
  deal_fields: z.array(DealFieldSchema),
})

export const CustomDealFieldValueSchema = z.object({
  deal_field_id: z.number(),
  value: z.string().nullable(),
})

export const CustomDealFieldSetSchema = z.object({
  id: z.number(),
  deal_field_set_id: z.number(),
  field_values: z.array(CustomDealFieldValueSchema),
})

export const ResponsibleLawyerSchema = z.object({
  id: z.number(),
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
  email: z.string(),
})

export const OrganizationSchema = z.object({
  id: z.number(),
  title: z.string(),
  require_responsible_lawyer: z.boolean(),
  show_banner: z.boolean(),
  banner_message: z.string(),
  days_to_delete_deals: z.number(),
  days_to_delete_users: z.number(),
  closing_book_link_expiration_days: z.number(),
  matter_number_state: z.string().optional().nullable(),
  client_number_state: z.string().optional().nullable(),
  deal_status_required: z.boolean().nullable(),
  auth_organization_id: z.number().nullable(),
  tfa_policy: z.number(),
  feature_flags: z.array(z.string()),
  available_dms: z.array(DMSSchema),
  deal_status_rule: z.enum(['enabled', 'disabled']).nullable(),
  deal_status_english_label: z.boolean().nullable(),
  deal_status_french_label: z.boolean().nullable(),
  deal_status_positions: z.array(z.number()).nullable(),
  deal_statuses: z.array(DealStatusSchema).nullable(),
  deal_invite_domain_rule: z.enum(['enabled', 'disabled']).optional(),
  default_snapshot_orientation: z.enum(['landscape', 'portrait']).nullable().optional(),
})

export const DealInviteDomainSchema = z.object({
  id: z.number(),
  domain: z.string(),
  organization_id: z.number(),
  is_internal: z.boolean(),
})

export const RoleSchema = z.enum(['VW', 'ED', 'AM'])

export const EmbeddedUserSchema = z.object({
  id: z.number(),
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
  email: z.string(),
  state: z.string(),
  date_joined: z.string(),
  deletion_date: z.string().nullable(),
  organization_id: z.number().optional(), // TODO: remove optional after confirming BE always returns organization_id for embedded user
})

export const DealUsershipSchema = z.object({
  id: z.number(),
  role: RoleSchema,
  email_frequency: z.string(),
  is_trashed: z.boolean(),
  expiry_date: z.string().nullable(),
  last_access: z.string().nullable(),
  user_id: z.number(),
  firm_id: z.number(),
  deal_id: z.number(),
  firm_abbreviation: z.string(),
  tfa_required: z.boolean(),
  support_ticket: z.boolean().optional(),
  user: EmbeddedUserSchema.optional(), // TODO: Remove optional after BE always returns user data
  is_deleted: z.boolean().optional(),
  has_access: z.boolean().optional(),
})

export const RedactedEmbeddedUserSchema = z.object({
  id: z.number(),
  gas_is_redacted: literal(true),
  first_name: z.string().nullable().optional(),
  last_name: z.string().nullable().optional(),
  email: z.string().optional(),
  state: z.string().optional(),
  date_joined: z.string().optional(),
  deletion_date: z.string().nullable().optional(),
})

export const RedactedUsershipSchema = z.object({
  id: z.number(),
  gas_is_redacted: literal(true),
  role: RoleSchema.nullable().optional(),
  email_frequency: z.string().nullable().optional(),
  is_trashed: z.boolean().nullable().optional(),
  expiry_date: z.string().nullable().optional(),
  last_access: z.string().nullable().optional(),
  user_id: z.number().nullable().optional(),
  firm_id: z.number().nullable().optional(),
  deal_id: z.number(),
  firm_abbreviation: z.string().nullable().optional(),
  tfa_required: z.boolean().nullable().optional(),
  user: RedactedEmbeddedUserSchema.optional(),
})

export const DealInviteSchema = z.object({
  deal_id: z.number(),
  email: z.string(),
  email_frequency: z.string(),
  firm_abbreviation: z.string(),
  firm_id: z.number(),
  id: z.number(),
  role: RoleSchema,
})

export const DealCreateSchema = z.object({
  title: z.string(),
  closing_date: z.string().nullable(),
  deal_status_id: z.number().nullable(),
  matter_number: z.string(),
  client_number: z.string(),
  responsible_lawyer_id: z.number().nullable().optional(),
  is_template: z.boolean(),
  is_org_wide: z.boolean().optional(),
  creation_status: z.enum(['ready', 'new']),
  source_deal_id: z.number().nullable().optional(),
  copy_parties: z.boolean().optional(),
  copy_schedules: z.boolean().optional(),
  copy_notes: z.boolean().optional(),
  copy_variables: z.boolean().optional(),
  copy_imanage: z.boolean().optional(),
  copy_versions: z.enum(['none', 'latest', 'final', 'first']).optional(),
  dms_id: z.union([z.string(), z.number()]).nullable().optional(),
  deal_field_set_values: z
    .array(
      z.object({
        deal_field_set_id: z.number().optional(),
        field_values: z.array(
          z.object({
            deal_field_id: z.number(),
            value: z.string().nullable(),
          }),
        ),
      }),
    )
    .optional(),
})

export const DealTemplateSchema = z.object({
  id: z.number(),
  deal_id: z.number(),
  region_id: z.number(),
  title: z.string(),
  is_org_wide: z.boolean(),
  created_at: z.string(),
})

export const DealTemplateResponseSchema = z.object({
  deal_template: DealTemplateSchema,
})

export const DealTemplatesResponseSchema = z.object({
  deal_templates: z.array(DealTemplateSchema),
})

export const SaveAsTemplatePayloadSchema = z.object({
  title: z.string(),
  copy_versions: z.enum(['none', 'latest', 'final', 'first']),
  copy_parties: z.boolean(),
  copy_schedules: z.boolean(),
  copy_notes: z.boolean(),
  copy_variables: z.boolean(),
  copy_imanage: z.boolean(),
  dms_id: z.union([z.string(), z.number()]).nullable().optional(),
})

export const DealSchema = z.object({
  client_number: z.string().nullable(),
  closing_date: z.string().nullable(),
  creation_status: z.union([z.literal('ready'), z.literal('new'), z.literal('importing')]),
  email: z.string(),
  feature_flags: z.array(z.string()),
  id: z.number(),
  is_template: z.boolean(),
  is_org_wide: z.boolean().optional(),
  matter_number: z.string().nullable(),
  organization_id: z.number(),
  owner_firm_id: z.number(),
  region_id: z.number(),
  title: z.string(),
  responsible_lawyer_id: z.number().nullish(),
  responsible_lawyer_user: ResponsibleLawyerSchema.nullish(),
  deal_status_id: z.number().nullable(),
  deal_status_updated_at: z.string().nullable(),
  preamble: z.string(),
  agenda_id: z.number(),
  action_item_status_positions: z.array(z.number()),
  agenda_is_empty: z.boolean(),
  always_show_overview: z.boolean(),
  approval_rule: z.enum(['enabled', 'disabled']),
  approver_ids: z.array(z.number()),
  category_ids: z.array(z.number()),
  closing_book_blurb: z.string(),
  closing_book_ids: z.array(z.number()),
  closing_book_title: z.string(),
  created_by_id: z.number(),
  owner_organization: z.string().nullable().optional(),
  created_by_name: z.string().nullable().optional(),
  created_by_email: z.string().nullable().optional(),
  created_by: EmbeddedUserSchema.pick({
    id: true,
    first_name: true,
    last_name: true,
    email: true,
  }).nullish(),
  deletion_date: z.string().nullable(),
  deletion_scheduled_by: z.number().nullable(),
  entity_ids: z.array(z.number()),
  firm_ids: z.array(z.number()),
  has_logo: z.boolean(),
  individual_ids: z.array(z.number()),
  item_approval_ids: z.array(z.number()),
  item_ids: z.array(z.number()),
  llm_features_enabled: z.boolean(),
  signature_page_lead_in_text: z.string(),
  signature_page_template_id: z.number(),
  snapshot_ids: z.array(z.number()),
  default_access_rule: z.string(),
  variable_access_rule: z.string(),
  group_access_rule: z.string().optional(),
  dms: DMSSchema.optional().nullable(),
  deal_invites: z.array(DealInviteSchema),
  userships: z.array(DealUsershipSchema),
  has_external_users: z.boolean().optional().nullable(),
  deal_field_set_values: z.array(CustomDealFieldSetSchema).optional(),
})

export const DealSchemaForDMS = z.object({
  deal_title: z.string(),
  deal_id: z.number(),
  last_access: z.nullable(z.string()),
})

export const DMSDocumentSchema = z.object({
  document_id: z.string(),
  name: z.string(),
  last_user_description: z.string(),
  edit_date: z.string(),
  file_size: z.number(),
  version: z.number(),
  extension: z.string(),
  workspace_id: z.number().nullable(),
})

export const ServiceSchema = z.union([
  z.literal('docusign'),
  z.literal('imanage'),
  z.literal('adobe'),
])

export const ClosingBookSchema = z.object({
  id: z.number(),
  deal_id: z.number(),
  label: z.string(),
  state: z.enum(['ready', 'in_progress', 'failed']),
  num_files: z.number(),
  created_by_id: z.number(),
  date_created: z.string(),
  gas_group_ids: z.array(z.number()),
  gas_is_public: z.boolean(),
  template_id: z.number().nullable(),
  description: z.string(),
  is_final: z.boolean(),
  state_updated_at: z.string(),
  closing_book_document_set_ids: z.array(z.number()),
})

export const NewClosingBookDataSchema = z.object({
  closing_book: z.object({
    allow_number_resetting: z.boolean(),
    cover_upload_id: z.string().nullable(),
    created_by_id: z.number().nullable(),
    date_created: z.string().nullable(),
    deal_id: z.string(),
    gas_group_ids: z.array(z.number()),
    gas_is_public: z.boolean(),
    label: z.string(),
    num_files: z.number().nullable(),
    positions: z.array(z.tuple([z.number(), z.string(), z.number()])),
    processing_failed: z.boolean(),
    retain_agenda_numbering: z.boolean(),
    state: z.string().nullable(),
    template_id: z.number().nullable(),
    closing_date: z.string().nullable(),
    closing_book_blurb: z.string().nullable(),
    closing_book_title: z.string().nullable(),
    custom_filenames_by_item_id: z.record(z.number(), z.string()).optional(),
  }),
})

export const ClosingBookDocumentSetSchema = z.object({
  id: z.number(),
  closing_book_id: z.number(),
  document_id: z.string().nullable(),
  created_by_id: z.number(),
  date_created: z.string(),
  date_uploaded: z.string().nullable(),
  library_id: z.string(),
  status: z.string(),
  host: z.string(),
  version: z.number().nullable(),
})

export const ClosingBookEventSchema = z.object({
  id: z.number(),
  closing_book_id: z.number(),
  created_by_id: z.number().nullable(),
  date_created: z.string(),
  expired_at: z.string().nullable(),
  content_type: z.enum(['upload', 'send', 'download-from-email']),
  sent_to_email: z.array(z.string()).nullable(),
  closing_book_document_set_id: z.number().nullable(),
})

export const ClosingBookTemplateSchema = z.object({
  id: z.number(),
  output_format: z.string(),
  permit_custom_cover: z.boolean(),
  title: z.string(),
  is_default_global_template: z.boolean(),
  organization_id: z.number().nullable(),
})

export const ClosingBookPreviewJobsSchema = z.object({
  job_ids: z.string().array(),
})

export const SubscribeToPusherSchema = z.object({
  auth: z.string(),
})

export const UntrashedObjectSchema = z.object({
  id: z.number(),
  is_trashed: z.boolean(),
})

export const TrashedUploadSchema = z.object({
  id: z.number(),
  deal_id: z.number(),
  document: z.object({
    extension: z.string(),
    filename: z.string(),
    extension_is_previewable: z.boolean(),
    uploaded_by: z.number(),
    source: z.string(),
    date_uploaded: z.string(),
    file_size: z.number().nullable(),
    preview_size: z.number().nullable(),
    num_pages: z.number().nullable(),
  }),
  dms: z.object({
    last_user: z.string().nullable(),
    file_size: z.number().nullable(),
    version: z.number().nullable(),
    version_edited_at: z.string().nullable(),
    doc_id: z.string().nullable(),
    name: z.string().nullable(),
    extension: z.string().nullable(),
    library_id: z.string().nullable(),
    workspace_id: z.number().nullable(),
  }),
  state: z.string(),
  is_trashed: z.boolean(),
  gas_is_public: z.boolean().optional(),
  gas_group_ids: z.array(z.number()).optional(),
})

export const TrashedVersionSchema = z.object({
  id: z.number(),
  item_id: z.number(),
  firm_id: z.number(),
  number: z.number(),
  is_trashed: z.boolean(),
  is_final: z.boolean(),
  gas_group_ids: z.array(z.number()).optional(),
  gas_is_public: z.boolean().optional(),
})

export const TrashedItemSchema = z.object({
  id: z.number(),
  deal_id: z.number(),
  title: z.string(),
  is_trashed: z.boolean(),
  gas_group_ids: z.array(z.number()),
  gas_is_public: z.boolean(),
})

export const TrashedActionItemSchema = z.object({
  id: z.number(),
  deal_id: z.number(),
  title: z.string(),
  is_trashed: z.boolean(),
  gas_group_ids: z.array(z.number()),
  gas_is_public: z.boolean(),
})

export const TrashedCategorySchema = z.object({
  id: z.number(),
  deal_id: z.number(),
  title: z.string(),
  is_trashed: z.boolean(),
  gas_group_ids: z.array(z.number()),
  gas_is_public: z.boolean(),
})

export const TrashedMarkupSchema = z.object({
  id: z.number(),
  version_id: z.number(),
  is_trashed: z.boolean(),
  gas_group_ids: z.array(z.number()).optional(),
  gas_is_public: z.boolean().optional(),
})

export const TrashedFirmSchema = z.object({
  id: z.number(),
  deal_id: z.number(),
  title: z.string(),
  abbreviation: z.string(),
  is_trashed: z.boolean(),
})

export const AllTrashSchema = z.object({
  uploads: z.array(TrashedUploadSchema),
  versions: z.array(TrashedVersionSchema),
  items: z.array(TrashedItemSchema),
  action_items: z.array(TrashedActionItemSchema).optional(),
  categories: z.array(TrashedCategorySchema),
  markups: z.array(TrashedMarkupSchema),
  firms: z.array(TrashedFirmSchema).optional(),
})

export const TrashedObjectsSchema = z.array(
  z.object({
    id: z.number(),
    name: z.string(),
    content_type: z.enum([
      'upload',
      'version',
      'item',
      'action-item',
      'category',
      'markup',
      'snapshot',
    ]),
    last_updated: z.string(),
    gas_group_ids: z.array(z.number()).nullable(),
    gas_is_public: z.boolean().nullable(),
    has_approvals: z.boolean(),
  }),
)

export const BulkUntrashResponseSchema = z.object({
  uploads: z.array(UntrashedObjectSchema).optional(),
  versions: z.array(UntrashedObjectSchema).optional(),
  items: z.array(UntrashedObjectSchema).optional(),
  action_items: z.array(UntrashedObjectSchema).optional(),
  categories: z.array(UntrashedObjectSchema).optional(),
  markups: z.array(UntrashedObjectSchema).optional(),
  snapshots: z.array(UntrashedObjectSchema).optional(),
  errors: z.array(
    z.object({
      id: z.number(),
      message: z.string(),
    }),
  ),
})
export const BulkTrashResponseSchema = BulkUntrashResponseSchema

export const BulkUntrashPayloadSchema = z.object({
  object_type: z.enum([
    'upload',
    'version',
    'item',
    'action_item',
    'category',
    'markup',
    'snapshot',
  ]),
  ids: z.array(z.number()),
})

export const UploadRawUrlSchema = z.object({
  id: z.string(),
  type: z.enum([
    'version',
    'executed-signature',
    'blank-signature-page',
    'signature-match',
    'schedule',
    'snapshot',
    'closing-version',
    'rendered-version',
    'blackline',
    'markup',
    'deal-logo',
    'closing-book',
    'unrendered-version',
    'pdf-signing-package-set',
    'upload',
    'agenda-import',
  ]),
  url_type: z.enum(['download_original', 'download_pdf', 'embed_pdf']),
})

export const UploadSchema = z.object({
  id: z.number(),
  state: z.string(),
  state_updated_at: z.string(),
  is_trashed: z.boolean(),
  deal_id: z.number(),
  file_match_ids: z.array(z.number()),
  is_matching: z.boolean(),
  document: z.object({
    extension: z.string(),
    filename: z.string(),
    extension_is_previewable: z.boolean(),
    uploaded_by: z.number(),
    source: z.string(),
    date_uploaded: z.string(),
    file_size: z.number().nullable(),
    preview_size: z.number().nullable(),
    num_pages: z.number().nullable(),
  }),
  dms: z.object({
    last_user: z.string().nullable(),
    file_size: z.number().nullable(),
    version: z.number().nullable(),
    version_edited_at: z.string().nullable(),
    doc_id: z.string().nullable(),
    name: z.string().nullable(),
    extension: z.string().nullable(),
    library_id: z.string().nullable(),
    workspace_id: z.number().nullable(),
  }),
  gas_is_public: z.boolean(),
  gas_group_ids: z.array(z.number()),
})

export const UploadResponseSchema = z.object({
  upload: UploadSchema,
})

export const FileMatchSchema = z.object({
  id: z.number(),
  accuracy: z.number(),
  upload: z.number(),
  item: z.number(),
})

export const FirmSchema = z.object({
  id: z.number(),
  deal_id: z.number(),
  name: z.string(),
  abbreviation: z.string(),
  usership_ids: z.array(z.number()),
  deal_invite_ids: z.array(z.number()),
  gas_is_public: z.boolean().nullable().optional(),
  gas_group_ids: z.array(z.number()).nullable().optional(),
  gas_is_redacted: z.boolean().nullable().optional(),
})

export const RedactedFirmSchema = z.object({
  id: z.number(),
  deal_id: z.number(),
  gas_is_redacted: z.boolean(),
})

export const AgendaNoteSchema = z.object({
  id: z.number(),
  date_created: z.string(),
  last_updated: z.string(),
  deal_id: z.number(),
  created_by_id: z.number(),
  item_id: z.number().nullable(),
  action_item_id: z.number().nullable(),
  content_type: z.enum(['item', 'action-item']),
  is_resolved: z.boolean(),
  body: z.string(),
  created_by_name: z.string(),
})

export const LastUnresolvedNote = z.object({
  id: z.number(),
  date_created: z.string(),
  last_updated: z.string(),
  body: z.string(),
  is_resolved: z.boolean(),
  created_by_id: z.number(),
  created_by_name: z.string(),
})

const DmsSaveHistorySchema = z.object({
  state: z.string(),
  version_id: z.number().nullable(),
  dms_doc_id: z.string().nullable(),
  dms_version: z.number().nullable(),
  dms_extension: z.string().nullable(),
  dms_updated_at: z.string().nullable(),
  dms_library_id: z.string().nullable(),
  dms_user: z.string().nullable(),
  dms_file_name: z.string().nullable(),
  dms_host: z.string().nullable(),
  is_rendered: z.boolean(),
})

export const SnapshotSchema = z.object({
  id: z.number(),
  number: z.number(),
  date_created: z.string(),
  state: z.enum(['ready', 'in-progress', 'failed']),
  state_updated_at: z.string(),
  deal_id: z.number(),
  created_by_id: z.number(),
  is_trashed: z.boolean(),
  label: z.string(),
  orientation: z.enum(['landscape', 'portrait']).nullable(),
  document: z
    .object({
      filename: z.string(),
      file_size: z.number().nullable(),
      preview_size: z.number().nullable(),
      extension: z.string(),
    })
    .nullable(),
  gas_is_public: z.boolean(),
  gas_group_ids: z.array(z.number()),
  gas_is_redacted: z.boolean().optional(),
})

export const RedactedSnapshotSchema = z.object({
  id: z.number(),
  number: z.number(),
  date_created: z.string(),
  deal_id: z.number(),
  is_trashed: z.boolean(),
  gas_is_redacted: z.boolean(),
})

export const ClosingVersionSchema = z.object({
  id: z.number(),
  created_by_id: z.number(),
  date_created: z.string(),
  item_id: z.number(),
  deal_id: z.number(),
  version_id: z.number(),
  ctype: z.enum(['final', 'partial', 'signed original', 'unsigned original']),
  state: z.enum(['in-progress', 'ready', 'failed']),
  document: z
    .object({
      filename: z.string(),
      file_size: z.number(),
      extension: z.string(),
    })
    .nullable(),
  dms_save_history: z
    .array(DmsSaveHistorySchema)
    .nullish()
    .transform(dmsSaveHistory => dmsSaveHistory ?? null),
})

export const VersionSchema = z.object({
  id: z.number(),
  date_created: z.string(),
  last_updated: z.string(),
  is_final: z.boolean(),
  number: z.number(),
  is_trashed: z.boolean(),
  detect_variable_status: z.string(),
  detect_signatures_status: z.string(),
  created_by_id: z.number(),
  item_id: z.number(),
  firm_id: z.number().nullable(),
  state: z.string(),
  state_updated_at: z.string(),
  dms_save_history: z
    .array(DmsSaveHistorySchema)
    .nullish()
    .transform(dmsSaveHistory => dmsSaveHistory ?? null),
  comment: z.string(),
  markup_ids: z.array(z.number()),
  gas_is_public: z.boolean(),
  gas_group_ids: z.array(z.number()),
  rendered_version_id: z.number().nullable(),
  is_minimized: z.boolean().optional().nullable(),
  document: DocumentSchema,
  dms: z
    .object({
      last_user: z.string().nullable(),
      file_size: z.number().nullable(),
      version: z.number().nullable(),
      version_edited_at: z.string().nullable(),
      doc_id: z.string().nullable(),
      name: z.string().nullable(),
      extension: z.string().nullable(),
      library_id: z.string().nullable(),
      workspace_id: z.number().nullable(),
    })
    .nullable(),
})

export const CreateVersionPayloadSchema = z.object({
  item_id: z.number(),
  upload_id: z.string(),
  firm_id: z.string(),
  comment: z.string(),
  is_final: z.boolean().optional(),
  track_new_document: z.boolean().optional(),
})

export const MarkupSchema = z.object({
  id: z.number(),
  comment: z.string(),
  date_created: z.string(),
  last_updated: z.string(),
  signed_off: z.boolean(),
  is_trashed: z.boolean(),
  document: DocumentSchema.nullable(),
  created_by_id: z.number(),
  version_id: z.number(),
  gas_group_ids: z.array(z.number()),
  gas_is_public: z.boolean(),
})

export const CreateMarkupPayloadSchema = z.object({
  version_id: z.number(),
  comment: z.string(),
  signed_off: z.boolean(),
  is_trashed: z.boolean(),
  upload_id: z.number().nullable(),
})

export type CreateMarkupPayload = z.infer<typeof CreateMarkupPayloadSchema>

export const BlacklineDocumentSchema = z.object({
  filename: z.string(),
  file_size: z.number().nullable(),
})

export const BlacklineSchema = z.object({
  id: z.number(),
  is_trashed: z.boolean(),
  deal_id: z.number(),
  content_type: z.enum(['version', 'snapshot']),
  state: z.string(),
  state_updated_at: z.string(),
  old_id: z.number(),
  new_id: z.number(),
  document: BlacklineDocumentSchema.nullable(),
})

export const AttachmentTypeSchema = z.enum([
  'unrendered-version',
  'rendered-version',
  'closing-version',
  'blackline',
  'schedule',
  'executed-signature',
  'markup',
  'snapshot',
])

export const AttachmentFormatSchema = z.enum(['original', 'pdf'])

export const SendDocumentsAttachmentSchema = z.object({
  id: z.number(),
  type: AttachmentTypeSchema,
  format: AttachmentFormatSchema,
})

export const ExportRequestSchema = z.object({
  include: z.array(z.string()),
  item_ids: z.array(z.number()),
  flatten: z.boolean(),
  render_state: z.enum(['rendered', 'unrendered', 'both']).optional(),
})

export const SendDocumentsRequestSchema = z.object({
  emails: z.array(z.string()),
  subject: z.string(),
  body: z.string(),
  attachments: z.array(SendDocumentsAttachmentSchema),
})

export const VersionMinimalSchema = z.object({
  id: z.number(),
  date_created: z.string(),
  is_final: z.boolean(),
  number: z.number(),
  is_trashed: z.boolean(),
  created_by_id: z.number(),
  item_id: z.number(),
  firm_id: z.number().nullable(),
  is_minimized: z.boolean().optional().nullable(),
  state: z.string(),
  document: z.object({
    extension: z.string(),
    filename: z.string(),
    extension_is_previewable: z.boolean(),
    uploaded_by: z.number(),
    source: z.string(),
    date_uploaded: z.string(),
    file_size: z.number().nullable(),
    num_pages: z.number().nullable(),
  }),
})

export const ItemSchema = z.object({
  id: z.number(),
  deal_id: z.number(),
  is_trashed: z.boolean(),
  ctype: z.string(),
  title: z.string(),
  long_title: z.string(),
  signature_page_template_id: z.number().nullable(),
  signature_page_footer_text: z.string(),
  signature_page_lead_in_text: z.string(),
  closing_version_id: z.number().nullable(),
  item_approval_id: z.number().nullable(),
  dms_last_user: z.string().nullable(),
  dms_file_size: z.number().nullable(),
  dms_status: z.string().nullable(),
  dms_current_version: z.number().nullable(),
  dms_current_version_edited_at: z.string().nullable(),
  dms_last_synced_at: z.string().nullable(),
  dms_name: z.string().nullable(),
  dms_extension: z.string().nullable(),
  dms_doc_id: z.string().nullable(),
  dms_library_id: z.string().nullable(),
  dms_host: z.string().nullable(),
  gas_is_public: z.boolean(),
  gas_group_ids: z.array(z.number()),
  version_ids: z.array(z.number()),
  signature_ids: z.array(z.number()),
  schedule_ids: z.array(z.number()),
  file_match_ids: z.array(z.number()),
  parent_item_ids: z.array(z.number()),
})

export const RedactedItemSchema = z.object({
  id: z.number(),
  deal_id: z.number(),
  is_trashed: z.boolean(),
  version_ids: z.array(z.number()),
  signature_ids: z.array(z.number()),
  schedule_ids: z.array(z.number()),
  file_match_ids: z.array(z.number()),
  parent_item_ids: z.array(z.number()),
  gas_is_redacted: z.boolean(),
})

export const ActionItemSchema = z.object({
  id: z.number(),
  deal_id: z.number(),
  is_trashed: z.boolean(),
  status: z.string(),
  action_item_status_id: z.number(),
  title: z.string(),
  long_title: z.string(),
  gas_group_ids: z.array(z.number()),
  gas_is_public: z.boolean(),
})

export const ActionItemStatusSchema = z.object({
  id: z.number(),
  deal_id: z.number(),
  title: z.string(),
  colour: z.number(),
  is_default: z.boolean(),
})

export const RedactedActionItemSchema = z.object({
  id: z.number(),
  deal_id: z.number(),
  is_trashed: z.boolean(),
  gas_is_redacted: z.boolean(),
})

export const CategorySchema = z.object({
  id: z.number(),
  deal_id: z.number(),
  is_trashed: z.boolean(),
  title: z.string(),
  long_title: z.string(),
  gas_is_public: z.boolean(),
  gas_group_ids: z.array(z.number()),
})

export const RedactedCategorySchema = z.object({
  id: z.number(),
  deal_id: z.number(),
  is_trashed: z.boolean(),
  gas_is_redacted: z.boolean(),
})

export const AuditTokenSchema = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string(),
  date_created: z.string(),
  last_accessed: z.string().nullable(),
  expires_at: z.string(),
  is_disabled: z.boolean(),
  created_by_id: z.number(),
  created_by_name: z.string(),
})

export const DealVariableSchema = z.object({
  id: z.number(),
  deal_id: z.number(),
  name: z.string(),
  value: z.string().nullable(),
  state: z.enum(['obsolete', 'partial', 'current']).nullable(),
  legacy: z.boolean(),
  current_total: z.number(),
  rendered_total: z.number(),
  unlinked_total: z.number(),
  item_variable_ids: z.array(z.number()),
})

export const ItemVariableSchema = z.object({
  id: z.number(),
  name: z.string(),
  legacy: z.boolean(),
  deal_variable_id: z.number(),
  item_id: z.number(),
  is_linked: z.boolean(),
  value: z.string().nullable(),
  render_state: z.enum(['current', 'unrendered', 'obsolete']).nullable(),
})

export const ItemWithVariableSchema = z.object({
  id: z.number(),
  latest_version_id: z.number().nullable(),
  item_variables: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
    }),
  ),
  unlinked_item_variable_total: z.number(),
  rendered_version: z.nullable(
    z.object({
      id: z.number(),
      state: z.string(),
    }),
  ),
  state: z.enum(['obsolete', 'partial', 'current']).nullable(),
})

export const RenderedVersionSchema = z.object({
  id: z.number(),
  num_pages: z.number().nullable(),
  source_version_id: z.number(),
  state: z.string(),
  state_updated_at: z.string(),
})

export const VersionRenderedVariableSchema = z.object({
  id: z.number(),
  version_id: z.number(),
  value: z.string().nullable(),
  item_variable_id: z.number(),
  is_most_recent: z.boolean(),
})

export const VersionDetectedVariableSchema = z.object({
  id: z.number(),
  item_variable_id: z.number(),
  version_id: z.number(),
})

export const ScheduleSchema = z.object({
  id: z.number(),
  deal_id: z.number(),
  item_id: z.number(),
  title: z.string(),
  attachment_type: z.enum(['file', 'item']),
  child_item_id: z.number().nullable(),
  document: DocumentSchema.nullable(),
  is_locked: z.boolean(),
  state: z.enum(['incomplete', 'complete']),
})

export const SigningPackageSchema = z.object({
  id: z.number(),
  status: z.enum(['sent', 'seen', 'failed', 'signed', 'unknown', 'created', 'obsolete']),
  status_updated_at: z.string().nullable(),
  signing_package_id: z.number(),
  individual_id: z.number(),
  package_type: z.enum(['pdf', 'docusign', 'onespan', 'imanage', 'adobe']),
  state: z.string(),
  created_by_name: z.string(),
  package_url: z.string().nullable(),
  signing_package_set_id: z.number(),
  number_of_signatures: z.number(),
  date_created: z.string(),
})

export const IndividualSchema = z.object({
  id: z.number(),
  deal_id: z.number(),
  signing_packages: z.array(SigningPackageSchema),
  name: z.string(),
  email: z.string(),
  signing_position_ids: z.array(z.number()),
})

export const EntitySchema = z.object({
  id: z.number(),
  deal_id: z.number(),
  name: z.string(),
  signing_position_ids: z.array(z.number()),
})

export const SigningPositionSchema = z.object({
  id: z.number(),
  ctype: z.enum(['officer', 'personal']),
  title: z.string(),
  individual_id: z.number().nullable(),
  entity_id: z.number().nullable(),
  deal_id: z.number(),
  signature_ids: z.array(z.number()),
})

export const SignatureSchema = z.object({
  id: z.number(),
  item_id: z.number(),
  status: z.enum(['current', 'obsolete', 'altered']),
  signature_page_id: z.number().nullable(),
  signing_position_id: z.number().nullable(),
  execution_state: z.enum([
    'unexecuted',
    'pdf',
    'legacy-file',
    'legacy-error',
    'signed-original-closing-version',
    'manual',
  ]),
  execution_state_updated_at: z.string(),
  executed_file_page_number: z.number().nullable(),
  execution_source_id: z.number().nullable(),
  x_position: z.number().nullable(),
  y_position: z.number().nullable(),
})

export const SignaturePageSchema = z.object({
  id: z.number(),
  item_id: z.number(),
  source_version_id: z.number().nullable(),
  source_page_number: z.number(),
  source: z.enum(['extracted', 'generated']),
  needs_review: z.boolean(),
  is_detecting: z.boolean(),
  raw_state: z.enum(['generating', 'ready', 'error']),
  raw_state_updated_at: z.string(),
  replacement_page_id: z.number().nullable(),
  signature_ids: z.array(z.number()),
})

export const SignaturePageTemplateSchema = z.object({
  id: z.number(),
  title: z.string(),
  organization_id: z.number().nullable(),
  is_default: z.boolean(),
})

const PartiesOnItemSchema = z.object({
  item_id: z.number(),
  individual_ids: z.array(z.number()),
  entity_ids: z.array(z.number()),
})

export const AddSignaturesRequestSchema = z.object({
  parties_to_items: z.array(PartiesOnItemSchema),
})

export const AddSignaturesResponseSchema = z.object({
  signatures: z.array(SignatureSchema),
  signature_pages: z.array(SignaturePageSchema),
})

export const UpdateSignatureCoordinatesSchema = z.object({
  x_position: z.number(),
  y_position: z.number(),
})

const FileObjectsDocumentSchema = z.object({
  filename: z.string(),
  file_size: z.number().nullable(),
  date_uploaded: z.string(),
  uploaded_by: z.number(),
  source: z.string().nullable(),
  extension: z.string(),
  extension_is_previewable: z.boolean(),
})

const compactIdArray = z
  .array(z.number().nullable())
  .transform(arr => arr.filter((id): id is number => id !== null))

export const FileObjectsRedactedItemSchema = z.object({
  id: z.number(),
  file_match_ids: compactIdArray,
})

export const FileObjectsVersionSchema = z.object({
  id: z.number(),
  item_id: z.number(),
  item_title: z.string(),
  number: z.number(),
  state: z.string(),
  gas_group_ids: compactIdArray,
  gas_is_public: z.boolean(),
  document: FileObjectsDocumentSchema,
})

export const FileObjectsUploadSchema = z.object({
  id: z.number(),
  state: z.string(),
  file_match_ids: compactIdArray,
  gas_group_ids: compactIdArray,
  gas_is_public: z.boolean(),
  document: FileObjectsDocumentSchema,
})

export const FileObjectsMarkupSchema = z.object({
  id: z.number(),
  version_id: z.number(),
  gas_group_ids: compactIdArray,
  gas_is_public: z.boolean(),
  document: FileObjectsDocumentSchema,
})

export const FileObjectsScheduleSchema = z.object({
  id: z.number(),
  item_id: z.number(),
  item_title: z.string(),
  gas_group_ids: compactIdArray,
  gas_is_public: z.boolean(),
  document: FileObjectsDocumentSchema,
})

export const FileObjectsResponseSchema = z.object({
  redacted_items: z.array(FileObjectsRedactedItemSchema).optional(),
  versions: z.array(FileObjectsVersionSchema),
  uploads: z.array(FileObjectsUploadSchema),
  markups: z.array(FileObjectsMarkupSchema),
  schedules: z.array(FileObjectsScheduleSchema),
  num_signature_pages_to_review: z.number(),
})

export type FileObjectsUpload = z.infer<typeof FileObjectsUploadSchema>
export type FileObjectsVersion = z.infer<typeof FileObjectsVersionSchema>
export type FileObjectsMarkup = z.infer<typeof FileObjectsMarkupSchema>
export type FileObjectsSchedule = z.infer<typeof FileObjectsScheduleSchema>
export type FileObjectsRedactedItem = z.infer<typeof FileObjectsRedactedItemSchema>
export type FileObjectsResponse = z.infer<typeof FileObjectsResponseSchema>
